import React from "react";
//#region types
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { DocumentDraftFormTypes } from "models/formTypes";
import { Amount } from "models/utilTypes/currencyType";
import { DocumentDraftListProps } from "models/propTypes/documentDraftPropTypes";
import { TableConstants } from "utils/constants";
//#endregion
//#region components
import { RoundButton, TableAsync, useFilterContext } from "components/common";
import DocumentDraftStatusTags from "./statusTags";
import { useNavigate, useLocation } from "react-router-dom";
import { useDocumentDraftService } from "serviceHooks";
import DocumentDraftTypeTag from "./typeTags";
import DocumentDraftSourceIcons from "./sourceIcons";
import DocumentDraftVoucherIcons from "./voucherIcons";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const DocumentDraftListView: React.FC<DocumentDraftListProps> = (props) => {
  const documentDraftService = useDocumentDraftService();
  const { filter } = useFilterContext<DocumentDraftFormTypes.Filter>();
  const location = useLocation();
  const navigate = useNavigate();

  const filterFunc = React.useMemo(
    () => (baseFilter: WintUtilsWebApi.Filter) => documentDraftService.reportFilter(baseFilter, filter),
    [filter]
  );

  const getItemUrl = (id: number | string) => UrlHelper.formatPath(`/DocumentDraft/${id}`);

  const onViewDocumentClick = (row: WintSuperkollAPIModel.DocumentDraft, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const url = documentDraftService.getDocumentUrl(row);
    if (url)
      navigate(UrlHelper.formatPath(url), {
        state: { background: location, preserveSearch: true },
      });
  };

  return (
    <>
      <TableAsync<WintSuperkollAPIModel.DocumentDraft>
        tableId={TableConstants.DocumentDraftList}
        properties={{
          source: {
            description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Source),
            formatter: (values: WintSuperkollAPIModel.DocumentDraft) => (
              <DocumentDraftSourceIcons source={values.source} />
            ),
            noWrap: true,
          },
          subject: {
            description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Info),
            formatter: (values: WintSuperkollAPIModel.DocumentDraft) =>
              values.subject && values.subject.trim().length > 0
                ? values.subject
                : values.attachments?.[0]?.fileName,
            noWrap: true,
            maxWidth: 120,
            disableSort: true,
          },
          from: {
            description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.From),
            formatter: (values: WintSuperkollAPIModel.DocumentDraft) =>
              values.from && values.from.trim().length > 0 ? values.from : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Uploaded),
            noWrap: true,
            maxWidth: 100,
          },
          created: {
            description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Received),
            formatter: (values: WintSuperkollAPIModel.DocumentDraft) => values.created && new Date(values.created),
            noWrap: true,
            classification: "date",
          },
          state: {
            description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Status),
            formatter: (values: WintSuperkollAPIModel.DocumentDraft) => (
              <DocumentDraftStatusTags status={values.state} />
            ),
            noWrap: true,
          },
          type: {
            description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Type),
            formatter: (values: WintSuperkollAPIModel.DocumentDraft) => <DocumentDraftTypeTag type={values.type} />,
            noWrap: true,
            maxWidth: 80,
          },
          amount: {
            description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Amount),
            formatter: (values: WintSuperkollAPIModel.DocumentDraft) => new Amount(values.currency, values.amount),
            className: cssExports.textBlack,
            alignment: "right",
            noWrap: true,
            disableSort: true,
          },
          entityId: {
            description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Voucher),
            formatter: (values: WintSuperkollAPIModel.DocumentDraft) =>
              (values.entityId || values.entityGuid) && values.isEntityViewable ? (
                <RoundButton className={cssExports.tableIconButton} onClick={onViewDocumentClick.bind(null, values)}>
                  <DocumentDraftVoucherIcons type={values.type} />
                </RoundButton>
              ) : (
                <></>
              ),
            alignment: "center",
            maxWidth: 80,
          },
        }}
        orderByProperty={"created"}
        orderByDescending
        filter={filterFunc}
        pagination="advanced"
        maxRowCount={10}
        itemUrl={getItemUrl}
        reloadKey={props.reloadKey}
      />
    </>
  );
};

export default DocumentDraftListView;
